/**
 * Fixtures para correr el portal sin credenciales ni red (`USE_MOCKS: true`).
 *
 * No devuelven modelos de vista ya armados: devuelven tareas y propiedades con
 * la forma de la API y las hacen pasar por el mismo `buildTrack` / `toIncident`
 * / `toWorkOrder` que la ruta real. Así el modo de desarrollo ejercita el
 * código que después corre en producción, en vez de un atajo que oculta bugs.
 *
 * Los datos salen del mapa de campos del spec §3 y de las referencias de
 * staging del §9, y están elegidos para que cada caso borde del checklist de
 * verificación se pueda ver en pantalla.
 */
import type { CotProperty, CotTask, Incident, ObjectId, Track, WorkOrder } from './types'
import { buildTrack, LIST_EXCLUDED_INCIDENTS, LIST_EXCLUDED_WORK_ORDERS, type CatalogEntry } from '@/domain/track'
import { incidentPropertyIds, toIncident, toWorkOrder, workOrderPropertyIds } from '@/domain/map'
import { PRIORITY_ORDER } from '@/domain/grouping'

const props = new Map<ObjectId, CotProperty>()
let seq = 0
const nextId = (): ObjectId =>
  (0x64f1e000 + ++seq * 1777).toString(16) + '10a145df0d' + ((seq * 97) % 4096).toString(16).padStart(4, '0') + 'c1'

/** `id` fijo solo cuando el código depende del id real (prioridades). */
function prop(code: string, display: string, propertyType?: string, id?: ObjectId): ObjectId {
  const _id = id ?? nextId()
  props.set(_id, { _id, name: { code, display }, propertyType, isActive: true })
  return _id
}

/* ---------- Catálogos de estado ---------- */

const INC_STATES: [string, string][] = [
  ['incidents_review_zone_leader', '0. En revisión: Jefe de Zona'],
  ['incidents_review_mantti', '1. En revisión: Mantto/TI'],
  ['incidents_process', '2. En proceso'],
  ['incidents_escalated_ti', '2.1. Escalado a TI'],
  ['incidents_taskcompleted', '3. Trabajos terminados'],
  ['incidents_works_review_mantti', '3.1. En Revisión Mantto'],
  ['incidents_closed', '4. Incidencia cerrada'],
  ['incidents_rejected', '5. Incidencia rechazada'],
]

const OT_STATES: [string, string][] = [
  ['ot_start_date_review', '0. En revisión fecha inicio'],
  ['ot_future_tasks', '0. Tareas futuras'],
  ['ot_assigned', '1. Asignada'],
  ['ot_checklist_pending', '1.2. Checklist pendiente'],
  ['ot_execution', '2. En ejecución'],
  ['ot_escalated_ti', '2.1. Escalado a TI'],
  ['ot_review', '3. En revisión'],
  ['ot_support_review', '3.1. En Revisión Soporte'],
  ['ot_budget_review', '3.2. En Revisión Presupuesto'],
  ['ot_extension_review', '3.3. En Revisión Prórroga'],
  ['ot_replacement_review', '3.4. En Revisión Repuesto'],
  ['ot_pre_closed', '4. Pre-cerrado'],
  ['ot_logistics_rejected', '4.1. Logística inversa rechazada'],
  ['ot_warehouse_confirm', '4.2. Confirmación de bodega'],
  ['ot_preliminary_closed', '5. Cerrado preliminar'],
  ['ot_closed_end', '6. Cerrado final'],
  ['ot_payment_process', '6.1. En proceso de pago'],
  ['ot_edp_confirmado', '6.2. Pago confirmado'],
  ['ot_sent_sap', '7. Enviada a SAP'],
  ['ot_cancelled', '8. Cancelada'],
]

const stateId = new Map<string, ObjectId>()
function catalog(list: [string, string][], type: string): CatalogEntry[] {
  return list.map(([code, display]) => {
    const id = prop(code, display, type)
    stateId.set(code, id)
    // El smState es una entidad distinta de su propiedad, con su propio id.
    return { code, display, propertyId: id, smStateId: nextId() }
  })
}

const incTrack: Track = buildTrack(catalog(INC_STATES, 'cm_n_state'), 'inc')
const otTrack: Track = buildTrack(catalog(OT_STATES, 'cm_cm_states'), 'ot')

/* ---------- Propiedades de negocio ---------- */

// Con los ids reales: el orden del listado se decide por id, así que con ids
// inventados los fixtures no ejercitarían el orden por prioridad.
const [ALTA, MEDIA_ALTA, MEDIA, BAJA] = PRIORITY_ORDER as [ObjectId, ObjectId, ObjectId, ObjectId]
const PRIORITY = {
  alta: prop('alta', 'Alta', 'data_priority', ALTA),
  mediaAlta: prop('media_alta', 'Media Alta', 'data_priority', MEDIA_ALTA),
  media: prop('media', 'Media', 'data_priority', MEDIA),
  baja: prop('baja', 'Baja', 'data_priority', BAJA),
}

// status2 · tipo_ut_nivel_3: la clave por la que se agrupa el listado.
const AREA = {
  noCombustible: prop('area_no_combustible', 'Área No Combustible', 'tipo_ut_nivel_3'),
  combustible: prop('area_combustible', 'Área Combustible', 'tipo_ut_nivel_3'),
  tienda: prop('tienda', 'Tienda', 'tipo_ut_nivel_3'),
}
const FILL_AREAS = [AREA.noCombustible, AREA.combustible, AREA.tienda]

const station = (code: string, display: string) => prop(code, display, 'ubicacion_tecnica_parent')
const family = (d: string) => prop(d.toLowerCase().replace(/\s+/g, '_'), d, 'familia_de_activo')
const activity = (d: string) => prop(d.toLowerCase().replace(/\s+/g, '_'), d, 'actividad')
const asset = (d: string) => prop(d.toLowerCase().replace(/\s+/g, '_'), d, 'asset')
const ut = (d: string) => prop(d.toLowerCase().replace(/\s+/g, '_'), d, 'ubicacion_tecnica_04')
const provider = (d: string) => prop(d.toLowerCase().replace(/[^a-z0-9]+/gi, '_'), d, 'provider')
const contract = (d: string) => prop(d.toLowerCase().replace(/\s+/g, '_'), d, 'contrato')

/* ---------- Construcción de tareas ---------- */

const NOW = Date.now()
const hoursAgo = (h: number) => new Date(NOW - h * 3_600_000).toISOString()
const daysAgo = (d: number) => new Date(NOW - d * 86_400_000).toISOString()

interface OtSpec {
  serial: number
  name: string
  state: string
  provider: string
  contract: string
  activity: string
  solution?: string
  start?: string
  end?: string
  noChannel?: boolean
}
interface IncSpec {
  serial: number
  name: string
  station: [string, string]
  family: string
  ut: string
  activity: string
  asset: string
  priority: ObjectId
  /** status2. Sin él, la incidencia cae en "Sin clasificar". */
  area?: ObjectId
  state: string
  createdAt: string
  ots: OtSpec[]
  /** Las órdenes van solo en incident_finished_ots. */
  finishedOnly?: boolean
  /** Estado legacy: no está en el catálogo. */
  legacyState?: boolean
  noChannel?: boolean
  /** Ids extra en los arreglos, para que otCount supere a las filas. */
  ghostOts?: number
}

const otTasks = new Map<ObjectId, CotTask>()
const otProps = new Map<ObjectId, CotProperty>()

function buildOt(spec: OtSpec): ObjectId {
  const taskId = nextId()
  const propId = nextId()

  const task: CotTask = {
    _id: taskId,
    name: spec.name,
    serial: spec.serial,
    channel: spec.noChannel ? undefined : nextId(),
    startDate: spec.start ?? undefined,
    endDate: spec.end ?? undefined,
    status: stateId.get(spec.state),
    status5: provider(spec.provider),
    extensions: {
      c_work_order: {
        ot_contract: contract(spec.contract),
        ot_activity: activity(spec.activity),
        ot_solution: spec.solution ?? 'No ha sido ingresada una solución',
      },
    },
  }
  // La propiedad de la OT apunta a su tarea por `owner`. Se alternan las dos
  // formas conocidas de la referencia de Mongo a propósito, para que el
  // normalizador se ejercite con las dos (corrección §4.1).
  const property: CotProperty = {
    _id: propId,
    name: { code: `ot_${spec.serial}`, display: spec.name },
    propertyType: 'm_work_order',
    owner:
      spec.serial % 2 === 0
        ? { $ref: 'tasks', $id: taskId }
        : { collection: 'tasks', oid: taskId },
  }

  otTasks.set(taskId, task)
  otProps.set(propId, property)
  props.set(propId, property)
  return propId
}

function buildIncident(spec: IncSpec): CotTask {
  const otIds = spec.ots.map(buildOt)
  const ghosts = Array.from({ length: spec.ghostOts ?? 0 }, () => nextId())
  const all = [...otIds, ...ghosts]

  return {
    _id: nextId(),
    name: spec.name,
    serial: spec.serial,
    channel: spec.noChannel ? undefined : nextId(),
    createdAt: spec.createdAt,
    status: spec.legacyState ? nextId() : stateId.get(spec.state),
    status1: station(spec.station[0], spec.station[1]),
    status2: spec.area,
    status3: family(spec.family),
    status4: activity(spec.activity),
    extensions: {
      c_incidentactive: {
        priority: spec.priority,
        incident_asset: asset(spec.asset),
        incident_ut: ut(spec.ut),
        incident_ots: spec.finishedOnly ? [] : all,
        incident_finished_ots: spec.finishedOnly ? all : [],
      },
    },
  }
}

const SPECS: IncSpec[] = [
  {
    serial: 3028,
    area: AREA.noCombustible,
    name: 'NORM - 3028 - ee_s089 - Red Sanitaria isla - Agua Potable',
    station: ['ee_s089', 'EDS : PUERTO MONTT/COSTANERA'],
    family: 'Sistema Red de Agua', ut: 'Red Sanitaria isla',
    activity: 'Filtración_Flexibles', asset: 'Agua Potable',
    priority: PRIORITY.baja, state: 'incidents_process', createdAt: hoursAgo(38),
    ots: [{
      serial: 4212, name: 'NORM - 4212 - Reparación red de agua isla 3',
      state: 'ot_assigned', provider: 'PELP INTERNACIONAL SA',
      contract: 'Pelp_Lavado', activity: 'Normativa Mantto',
      start: daysAgo(1), end: daysAgo(-3),
    }],
  },
  {
    serial: 4798,
    area: AREA.combustible,
    name: 'CORR - 4798 - ee_s112 - Islas 1 y 2 - Sistema Eléctrico',
    station: ['ee_s112', 'EDS : VIÑA DEL MAR/AGUA SANTA'],
    family: 'Sistema Eléctrico', ut: 'Islas 1 y 2',
    activity: 'Falla Eléctrica', asset: 'Iluminación de islas',
    priority: PRIORITY.media, state: 'incidents_process', createdAt: hoursAgo(14),
    ots: [
      { serial: 4801, name: 'CORR - 4801 - Revisión tablero eléctrico islas',
        state: 'ot_execution', provider: 'Climahouse Handyman', contract: 'Climahouse_Correctivo',
        activity: 'Correctivo Eléctrico',
        solution: 'Se reemplazó el contactor del circuito de islas; pendiente prueba nocturna.',
        start: daysAgo(1), end: daysAgo(-1) },
      // Estado de rama 3.2: enciende el círculo 3 y muestra el badge.
      { serial: 4802, name: 'CORR - 4802 - Recambio luminarias LED isla 2',
        state: 'ot_budget_review', provider: 'Tecno Eléctrica SpA', contract: 'TecnoElec_Marco',
        activity: 'Correctivo Eléctrico',
        solution: 'A la espera de aprobación de presupuesto para recambio de 8 luminarias.',
        start: daysAgo(2), end: daysAgo(-5) },
      { serial: 4803, name: 'CORR - 4803 - Diagnóstico surtidor isla 1',
        state: 'ot_start_date_review', provider: 'Gilbarco Veeder-Root',
        contract: 'Gilbarco_Servicio', activity: 'Diagnóstico Equipos' },
    ],
  },
  {
    serial: 2907,
    area: AREA.noCombustible,
    name: 'NORM - 2907 - ee_s054 - Área lavado - Bomba de presión',
    station: ['ee_s054', 'EDS : SANTA ROSA/CICARELLI'],
    family: 'Sistema de Lavado', ut: 'Área lavado',
    activity: 'Mantención_Bomba', asset: 'Bomba de presión',
    priority: PRIORITY.mediaAlta, state: 'incidents_taskcompleted', createdAt: daysAgo(9),
    finishedOnly: true, // sus OTs viven solo en incident_finished_ots
    ots: [{
      serial: 3990, name: 'NORM - 3990 - Mantención mayor bomba de presión',
      state: 'ot_preliminary_closed', provider: 'SERVICIOS INDUSTRIALES DEL SUR LTDA',
      contract: 'SISUR_Mantto', activity: 'Mantención Preventiva',
      solution: 'Se reemplazó el sello mecánico y se calibró el presostato. Equipo operativo.',
      start: daysAgo(6), end: daysAgo(2),
    }],
  },
  {
    serial: 3115,
    area: AREA.noCombustible,
    name: 'NORM - 3115 - ee_s031 - Patio - Señalética normativa',
    station: ['ee_s031', 'EDS : LA SERENA/BALMACEDA'],
    family: 'Obras Civiles', ut: 'Patio de maniobras',
    activity: 'Señalética', asset: 'Señalética normativa',
    priority: PRIORITY.baja, state: 'incidents_process', createdAt: daysAgo(210),
    legacyState: true, noChannel: true, // degrada a pipeline gris y sin enlace
    ots: [{
      serial: 2210, name: 'NORM - 2210 - Reposición de señalética de patio',
      state: 'ot_closed_end', provider: 'IMAGEN CORPORATIVA NORTE SPA',
      contract: 'ICN_Marco', activity: 'Obras Menores',
      solution: 'Se repusieron 6 señales verticales y demarcación horizontal.',
      start: daysAgo(190), end: daysAgo(180),
    }],
  },
  {
    serial: 3140,
    area: AREA.tienda,
    name: 'CORR - 3140 - ee_s077 - Baños públicos - Griferías',
    station: ['ee_s077', 'EDS : TEMUCO/CAUPOLICÁN'],
    family: 'Red Sanitaria', ut: 'Baños públicos',
    activity: 'Filtración_Griferías', asset: 'Griferías',
    priority: PRIORITY.alta, state: 'incidents_review_zone_leader', createdAt: hoursAgo(3),
    ots: [], // sin órdenes: el 20,4% de la población
  },
  {
    serial: 3202,
    area: AREA.noCombustible,
    name: 'NORM - 3202 - ee_s019 - Sala de máquinas - Compresor',
    station: ['ee_s019', 'EDS : ANTOFAGASTA/BALMACEDA'],
    family: 'Aire Comprimido', ut: 'Sala de máquinas',
    activity: 'Ruido_Anormal', asset: 'Compresor',
    priority: PRIORITY.media, state: 'incidents_works_review_mantti', createdAt: daysAgo(5),
    ghostOts: 2, // otCount 4 contra 2 filas: el contador incluye OTs de baja
    ots: [
      { serial: 4455, name: 'NORM - 4455 - Cambio de rodamientos compresor',
        state: 'ot_review', provider: 'AIRE INDUSTRIAL LTDA', contract: 'AireInd_Mantto',
        activity: 'Correctivo Mecánico',
        solution: 'Se reemplazaron los rodamientos del cabezal. En revisión de cierre.',
        start: daysAgo(3), end: daysAgo(1) },
      { serial: 4456, name: 'NORM - 4456 - Análisis de vibraciones',
        state: 'ot_sent_sap', provider: 'PREDICTIVA CHILE SPA', contract: 'Predictiva_Marco',
        activity: 'Predictivo',
        solution: 'Informe de vibraciones entregado. Sin observaciones adicionales.',
        start: daysAgo(4), end: daysAgo(3) },
    ],
  },
  /*
   * El contador dice 2 y no se lista ninguna fila: una orden cancelada y un id
   * fantasma. Es el caso que la sección 3 tiene que explicar en vez de decir
   * "todavía no hay órdenes", que sería falso.
   */
  {
    serial: 3260,
    // Sin status2: cae en "Sin clasificar", que va al final del listado.
    name: 'CORR - 3260 - ee_s103 - Cancha - Portón de acceso',
    station: ['ee_s103', 'EDS : OSORNO/REPÚBLICA'],
    family: 'Obras Civiles', ut: 'Cancha de estacionamiento',
    activity: 'Portón_Atascado', asset: 'Portón de acceso',
    priority: PRIORITY.media, state: 'incidents_process', createdAt: hoursAgo(52),
    ghostOts: 1,
    ots: [{
      serial: 4470, name: 'CORR - 4470 - Reparación de portón',
      state: 'ot_cancelled', provider: 'Climahouse Handyman',
      contract: 'Climahouse_Correctivo', activity: 'Obras Menores',
    }],
  },
  // Excluida del listado: su estado está en LIST_EXCLUDED_INCIDENTS.
  {
    serial: 2811,
    area: AREA.noCombustible,
    name: 'NORM - 2811 - ee_s044 - Pista - Pavimento',
    station: ['ee_s044', 'EDS : RANCAGUA/CARRETERA'],
    family: 'Obras Civiles', ut: 'Pista de acceso',
    activity: 'Pavimento_Grietas', asset: 'Pavimento',
    priority: PRIORITY.baja, state: 'incidents_closed', createdAt: daysAgo(30),
    ots: [],
  },
  /*
   * También excluida, y por un camino distinto al de la cerrada: su estado
   * está fuera del track, así que `resolveState` lo devuelve como desconocido.
   * Mientras ese resultado traía `code: null`, el filtro del listado no la
   * reconocía y la incidencia se listaba como "Estado no disponible".
   */
  {
    serial: 2795,
    area: AREA.combustible,
    name: 'CORR - 2795 - ee_s062 - Isla 4 - Surtidor duplicado',
    station: ['ee_s062', 'EDS : CONCEPCIÓN/PAICAVÍ'],
    family: 'Sistema de Despacho', ut: 'Isla 4',
    activity: 'Falla_Surtidor', asset: 'Surtidor',
    priority: PRIORITY.media, state: 'incidents_rejected', createdAt: daysAgo(11),
    ots: [],
  },
]

/* ---------- Relleno, para que el listado llegue al tope ---------- */

const FILL_STATIONS: [string, string][] = [
  ['ee_s044', 'EDS : RANCAGUA/CARRETERA'], ['ee_s062', 'EDS : CONCEPCIÓN/PAICAVÍ'],
  ['ee_s008', 'EDS : IQUIQUE/HÉROES'], ['ee_s091', 'EDS : VALDIVIA/PICARTE'],
  ['ee_s017', 'EDS : COPIAPÓ/LOS CARRERA'], ['ee_s103', 'EDS : OSORNO/REPÚBLICA'],
  ['ee_s058', 'EDS : CHILLÁN/O’HIGGINS'], ['ee_s002', 'EDS : ARICA/DIEGO PORTALES'],
  ['ee_s119', 'EDS : QUILPUÉ/FREIRE'], ['ee_s130', 'EDS : PUNTA ARENAS/BULNES'],
  ['ee_s070', 'EDS : TALCA/DOS NORTE'], ['ee_s013', 'EDS : CALAMA/GRANADEROS'],
]
const FILL_KIT: [string, string, string, string][] = [
  ['Sistema Red de Agua', 'Red Sanitaria isla', 'Filtración_Flexibles', 'Agua Potable'],
  ['Sistema Eléctrico', 'Islas de despacho', 'Falla Eléctrica', 'Iluminación'],
  ['Sistema de Lavado', 'Túnel de lavado', 'Mantención_Bomba', 'Bomba de presión'],
  ['Obras Civiles', 'Patio de maniobras', 'Pavimento_Grietas', 'Pavimento'],
  ['Aire Comprimido', 'Sala de máquinas', 'Ruido_Anormal', 'Compresor'],
]
const FILL_INC_STATES = [
  'incidents_review_zone_leader', 'incidents_review_mantti',
  'incidents_process', 'incidents_taskcompleted', 'incidents_escalated_ti',
]
const FILL_OT_STATES = [
  'ot_start_date_review', 'ot_assigned', 'ot_execution', 'ot_review',
  'ot_support_review', 'ot_pre_closed', 'ot_preliminary_closed', 'ot_closed_end',
]
const FILL_PROVIDERS = [
  'PELP INTERNACIONAL SA', 'Climahouse Handyman', 'Tecno Eléctrica SpA',
  'Gilbarco Veeder-Root', 'AIRE INDUSTRIAL LTDA', 'FRÍO Y CLIMA SPA',
]
const FILL_PRIORITIES = [PRIORITY.alta, PRIORITY.mediaAlta, PRIORITY.media, PRIORITY.baja]

function pushFill(i: number, createdAt: string) {
  const st = FILL_STATIONS[i % FILL_STATIONS.length]!
  const kit = FILL_KIT[i % FILL_KIT.length]!
  const serial = 3300 + i * 7
  const otCount = i % 5 === 0 ? 0 : i % 7 === 3 ? 2 : 1
  SPECS.push({
    serial,
    name: `${i % 2 ? 'CORR' : 'NORM'} - ${serial} - ${st[0]} - ${kit[1]} - ${kit[3]}`,
    station: st, family: kit[0], ut: kit[1], activity: kit[2], asset: kit[3],
    priority: FILL_PRIORITIES[i % 4]!,
    area: i % 11 === 10 ? undefined : FILL_AREAS[i % FILL_AREAS.length],
    state: FILL_INC_STATES[i % FILL_INC_STATES.length]!,
    createdAt,
    ots: Array.from({ length: otCount }, (_, j) => ({
      serial: 4900 + i * 3 + j,
      name: `${kit[2].replace(/_/g, ' ')} — ${kit[1].toLowerCase()}`,
      state: FILL_OT_STATES[(i * 2 + j) % FILL_OT_STATES.length]!,
      provider: FILL_PROVIDERS[(i + j) % FILL_PROVIDERS.length]!,
      contract: `Contrato_Marco_${(i % 4) + 1}`,
      activity: kit[2].replace(/_/g, ' '),
      start: daysAgo(6 + (i % 9)), end: daysAgo(1 + (i % 4)),
    })),
  })
}

for (let i = 0; i < 30; i++) pushFill(i, daysAgo(2 + i * 1.7))
// Relleno viejo, más antiguo que todo caso borde de arriba (el más viejo es la
// 3115, con estado legacy, a 210 días): el listado supera LIST_LIMIT y se ve
// la nota de truncado sin que el corte se lleve ninguno de esos casos.
for (let i = 30; i < 105; i++) pushFill(i, daysAgo(220 + (i - 30) * 2))

const INCIDENT_TASKS: CotTask[] = SPECS.map(buildIncident).sort(
  (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
)

/* ---------- Repositorio simulado ---------- */

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

export const mockTracks = { incidents: incTrack, workOrders: otTrack }

/**
 * Toda la historia listable, de la más nueva a la más vieja. El tope lo
 * aplica el repositorio con `capListing`, igual que en la ruta real.
 */
export async function mockLoadIncidents(): Promise<Incident[]> {
  await delay(320)
  return INCIDENT_TASKS.map((t) => toIncident(t, incTrack, props)).filter(
    (inc) => !inc.state.code || !LIST_EXCLUDED_INCIDENTS.has(inc.state.code),
  )
}

/** Relee una incidencia, como haría `GET /api/v1/tasks/{tg}/task/{id}`. */
export async function mockReloadIncident(incidentId: string): Promise<Incident | null> {
  await delay(180)
  const task = INCIDENT_TASKS.find((t) => t._id === incidentId)
  if (!task) return null
  const incident = toIncident(task, incTrack, props)
  // Igual que la ruta real: si dejó de pertenecer al listado, se avisa con null.
  return !incident.state.code || !LIST_EXCLUDED_INCIDENTS.has(incident.state.code)
    ? incident
    : null
}

export async function mockLoadWorkOrders(incident: Incident): Promise<WorkOrder[]> {
  await delay(260)
  const { readOwnerId } = await import('@/domain/owner')

  const out: WorkOrder[] = []
  for (const propId of incident.otPropertyIds) {
    const property = otProps.get(propId)
    if (!property) continue // id fantasma: OT dada de baja que el contador sí cuenta
    const owner = readOwnerId(property.owner)
    if (!owner.ok) continue
    const task = otTasks.get(owner.id)
    if (!task) continue
    out.push(toWorkOrder(task, property, otTrack, props))
  }
  return out.filter((ot) => !ot.state.code || !LIST_EXCLUDED_WORK_ORDERS.has(ot.state.code))
}

// Referenciadas para que el typechecker no marque los helpers como muertos:
// documentan qué ids necesitaría resolver la ruta real.
void incidentPropertyIds
void workOrderPropertyIds
